import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import AppTable from '../../../components/AppTable/AppTable';
import { setPagination } from '../../../redux/Actions/TableAction';
import { searchStockTransaction, cancelStockTransaction } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';
import TableSearch from '../../../components/TableSearch/TableSearch';

function StockTransferHistory({ isModal = false, onClose, onDataChange, fromLocation = null, productType = null }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Use local state for modal to avoid conflicts with warehouse data
    const [localTransferData, setLocalTransferData] = useState([]);
    const [localPagination, setLocalPagination] = useState({
        total: 0,
        page: 1,
        limit: 10
    });

    const transferHistoryData = isModal ? localTransferData : (Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []);

    // Local state for loading and error handling
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    
    // Bulk operations state
    const [selectedTransfers, setSelectedTransfers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Permission checks
    const canReadTransfers = true;
    const canCancelTransfers = true;

    // Initialize with empty array to prevent map errors
    useEffect(() => {
        if (!Array.isArray(ApiReducer.apiJson)) {
            dispatch(setApiJson([]));
        }
    }, []);

    // Memoized function to search stock transfer transactions
    const searchTransferHistory = useCallback(async (searchKey, searchValue) => {
        setIsLoading(true);
        setIsSearching(true);
        setError(null);

        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                status: 'transferd', // Filter for transfer transactions only,
                $nor: [{ status: 'rejected' }] // Exclude rejected transfers
            };

            // Add location filter if provided
            if (fromLocation) {
                searchObj.transferFrom = fromLocation;
            }

            // Add product type filter if provided
            if (productType) {
                searchObj.type = productType;
            }

            // Only add search key/value if both are provided
            if (searchKey && searchValue) {
                // Handle product-related searches with population
                const productFields = ['productName', 'brand', 'productType'];
                const transactionFields = ['status', 'place', 'stockLocation', 'transferFrom', 'transactionReason'];
                
                if (productFields.includes(searchKey)) {
                    // For product fields, search in populated productId field
                    searchObj[`productId.${searchKey}`] = {
                        $regex: searchValue,
                        $options: 'i'
                    };
                } else if (transactionFields.includes(searchKey)) {
                    // Handle text transaction fields
                    searchObj[searchKey] = {
                        $regex: searchValue,
                        $options: 'i'
                    };
                } else if (searchKey === 'transferQuantity' || searchKey === 'stockIn') {
                    // Handle numeric fields
                    searchObj[searchKey] = parseFloat(searchValue) || 0;
                }
            }

            const currentPagination = isModal ? localPagination : TableDataReducer.pagination;
            
            const json = {
                page: currentPagination.page,
                limit: currentPagination.limit,
                search: searchObj,
                populate: ['productId'] // Ensure product data is populated
            };

            const res = await HitApi(json, searchStockTransaction);

            // Handle response
            let transferData = [];
            let totalCount = 0;

            if (res?.statusCode === 200) {
                // Transaction API response structure
                transferData = res?.data?.docs || res?.transactions || res?.result || [];
                totalCount = res?.data?.totalDocs || res?.total || 0;

                // Ensure we have an array
                if (!Array.isArray(transferData)) {
                    console.warn("Stock Transaction API returned non-array data:", transferData);
                    transferData = [];
                }

                // Transform transaction data for display
                const historyData = transferData.map(transaction => ({
                    _id: transaction._id,
                    productId: transaction.productId?._id || transaction.productId,
                    productName: transaction.productId?.productName || 'Unknown Product',
                    brand: transaction.productId?.brand || '',
                    productType: transaction.productId?.productType || '',
                    productImageUrl: transaction.productId?.productImageUrl || '',
                    transferQuantity: transaction.transferQuantity || 0,
                    stockIn: transaction.stockIn || 0,
                    stockOut: transaction.stockOut || 0,
                    status: transaction.status || 'transferd',
                    place: transaction.place || 'warehouse',
                    stockLocation: transaction.stockLocation || 'warehouse',
                    transferFrom: transaction.transferFrom || '',
                    transactionReason: transaction.transactionReason || '',
                    transferNotes: transaction.transferNotes || '',
                    type: transaction.type || 'professional',
                    createdAt: transaction.createdAt,
                    updatedAt: transaction.updatedAt
                }));

                if (isModal) {
                    // Use local state for modal
                    setLocalTransferData(historyData);
                    setLocalPagination({
                        total: totalCount,
                        page: currentPagination.page,
                        limit: currentPagination.limit
                    });
                } else {
                    // Use Redux for standalone page
                    dispatch(setApiJson(historyData));
                    
                    // Update pagination data in Redux
                    const paginationData = {
                        total: totalCount,
                        page: TableDataReducer.pagination.page,
                        limit: TableDataReducer.pagination.limit
                    };
                    
                    dispatch(setPagination(paginationData));
                }
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to fetch transfer history';
                console.error("Stock Transaction API Error:", errorMessage);
                setError(errorMessage);
                
                if (isModal) {
                    setLocalTransferData([]);
                } else {
                    dispatch(setApiJson([]));
                }
                
                toast.error('Failed to fetch transfer history. Please try again.');
            }
        } catch (error) {
            console.error("Error fetching transfer history:", error);
            setError('Failed to fetch transfer history. Please check your connection and try again.');
            
            if (isModal) {
                setLocalTransferData([]);
            } else {
                dispatch(setApiJson([]));
            }
            
            toast.error('Error fetching transfer history. Please try again.');
        } finally {
            setIsLoading(false);
            setIsSearching(false);
        }
    }, [dispatch, TableDataReducer.pagination.page, TableDataReducer.pagination.limit, isModal, localPagination.page, localPagination.limit, fromLocation, productType]);

    useEffect(() => {
        if (canReadTransfers) {
            searchTransferHistory();
        }
    }, [searchTransferHistory, canReadTransfers]);

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }

        let searchValue = valueSearched.trim();

        // Convert numeric fields to numbers
        const numericFields = ['transferQuantity', 'stockIn'];
        if (numericFields.includes(optionSelect)) {
            const numericValue = parseFloat(searchValue);
            if (isNaN(numericValue)) {
                toast.error('Please enter a valid number for numeric fields');
                return;
            }
            searchValue = numericValue;
        }

        searchTransferHistory(optionSelect, searchValue);
    };

    // Cancel transfer functionality
    const handleCancelTransfer = async (transferId, productName) => {
        if (!transferId) {
            toast.error('Transfer ID is required to cancel the transfer');
            return;
        }

        const confirmCancel = window.confirm(
            `Are you sure you want to cancel the transfer for "${productName}"? This action cannot be undone.`
        );

        if (!confirmCancel) return;

        setIsLoading(true);
        
        try {
            const json = {
                _id: transferId
            };

            const res = await HitApi(json, cancelStockTransaction);

            if (res?.statusCode === 200) {
                toast.success('Transfer cancelled successfully');
                // Refresh the transfer history
                searchTransferHistory();
                // Notify parent to update transfer count
                if (onDataChange) onDataChange();
            } else {
                const errorMessage = res?.message || res?.error || 'Failed to cancel transfer';
                console.error("Cancel Transfer Error:", errorMessage);
                toast.error('Failed to cancel transfer. Please try again.');
            }
        } catch (error) {
            console.error("Error cancelling transfer:", error);
            toast.error('Error cancelling transfer. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Bulk operations
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            const allTransferIds = transferHistoryData.map(transfer => transfer._id);
            setSelectedTransfers(allTransferIds);
        } else {
            setSelectedTransfers([]);
        }
    };

    const handleSelectTransfer = (transferId, checked) => {
        if (checked) {
            setSelectedTransfers(prev => [...prev, transferId]);
        } else {
            setSelectedTransfers(prev => prev.filter(id => id !== transferId));
            setSelectAll(false);
        }
    };

    // Update selectAll state when individual checkboxes change
    React.useEffect(() => {
        if (transferHistoryData.length > 0) {
            const allSelected = transferHistoryData.every(transfer => 
                selectedTransfers.includes(transfer._id)
            );
            setSelectAll(allSelected);
        }
    }, [selectedTransfers, transferHistoryData]);

    // Bulk cancel transfers
    const handleBulkCancelTransfers = async () => {
        if (selectedTransfers.length === 0) {
            toast.error('Please select transfers to cancel');
            return;
        }

        const confirmCancel = window.confirm(
            `Are you sure you want to cancel ${selectedTransfers.length} selected transfer(s)? This action cannot be undone.`
        );

        if (!confirmCancel) return;

        setIsLoading(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            // Process each transfer cancellation
            for (const transferId of selectedTransfers) {
                try {
                    const json = { _id: transferId };
                    const res = await HitApi(json, cancelStockTransaction);
                    
                    if (res?.statusCode === 200) {
                        successCount++;
                    } else {
                        errorCount++;
                        console.error(`Failed to cancel transfer ${transferId}:`, res?.message);
                    }
                } catch (error) {
                    errorCount++;
                    console.error(`Error cancelling transfer ${transferId}:`, error);
                }
            }

            // Show results
            if (successCount > 0) {
                toast.success(`Successfully cancelled ${successCount} transfer(s)`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to cancel ${errorCount} transfer(s)`);
            }

            // Reset selection and refresh
            setSelectedTransfers([]);
            setSelectAll(false);
            searchTransferHistory();
            // Notify parent to update transfer count
            if (onDataChange) onDataChange();
            
        } catch (error) {
            console.error("Error in bulk cancel operation:", error);
            toast.error('Error processing bulk cancellation');
        } finally {
            setIsLoading(false);
        }
    };

    // Define transfer history data - moved to top to avoid reference errors

    // Clear selection when data changes
    React.useEffect(() => {
        setSelectedTransfers([]);
        setSelectAll(false);
    }, [transferHistoryData]);

    // Search options for transfer history
    const options = [
        { label: 'Product Name', value: 'productName' },
        { label: 'Brand', value: 'brand' },
        { label: 'Product Type', value: 'productType' },
        { label: 'Transfer Quantity', value: 'transferQuantity' },
        { label: 'Stock In', value: 'stockIn' },
        { label: 'Status', value: 'status' },
        { label: 'Current Location', value: 'stockLocation' },
        { label: 'Transfer From', value: 'transferFrom' },
        { label: 'Transfer Reason', value: 'transactionReason' },
    ];

    // Format date
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    const currentPaginationForIndex = isModal ? localPagination : TableDataReducer.pagination;
    const baseIndex = (currentPaginationForIndex.page - 1) * currentPaginationForIndex.limit;

    // Define transfer history table headers
    const tableHeaders = [
        {
            title: (
                <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
            ),
            key: "checkbox",
            width: "50px",
            align: "center",
            priority: 1,
            render: (_, row) => (
                <input
                    type="checkbox"
                    checked={selectedTransfers.includes(row._id)}
                    onChange={(e) => handleSelectTransfer(row._id, e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
            )
        },
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "start",
            priority: 1,
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Product",
            key: "productName",
            priority: 1,
            render: (value, row) => (
                <div className="flex items-start">
                    {row.productImageUrl ? (
                        <img
                            src={row.productImageUrl}
                            alt={value || 'Product'}
                            className="w-10 h-10 rounded-lg mr-3 object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div className="w-10 h-10 rounded-lg bg-blue-200 flex items-center justify-center mr-3 flex-shrink-0"
                        style={{ display: row.productImageUrl ? 'none' : 'flex' }}>
                        <span className="text-lg">🔄</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate" title={value}>
                            {value || 'Unnamed Product'}
                        </div>
                        {row.brand && (
                            <div className="text-sm text-gray-500 truncate mt-1" title={row.brand}>
                                {row.brand}
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: "Transfer Details",
            key: "transferDetails",
            align: "center",
            priority: 1,
            render: (_, row) => (
                <div className="text-center">
                    <div className="font-medium px-2 py-1 rounded text-sm mb-1 bg-blue-100 text-blue-800">
                        Qty: {row.transferQuantity}
                    </div>
                    <div className="text-xs text-gray-600">
                        <div>Stock In: {row.stockIn}</div>
                        <div>Stock Out: {row.stockOut}</div>
                    </div>
                </div>
            )
        },
        {
            title: "Transfer Route",
            key: "transferRoute",
            align: "start",
            priority: 2,
            render: (_, row) => (
                <div className="text-sm">
                    <div className="flex items-center mb-1">
                        <span className="text-gray-500">From:</span>
                        <span className="ml-1 font-medium capitalize">
                            {row.transferFrom || 'Unknown'}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <span className="text-gray-500">To:</span>
                        <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                            row.stockLocation === 'warehouse' 
                                ? 'bg-blue-100 text-blue-800' 
                                : row.stockLocation === 'salon'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-purple-100 text-purple-800'
                        }`}>
                            {row.stockLocation === 'warehouse' ? 'Warehouse' : 
                             row.stockLocation === 'salon' ? 'Salon' : 
                             row.stockLocation === 'brand' ? 'Brand' : row.stockLocation}
                        </span>
                    </div>
                </div>
            )
        },
        {
            title: "Status",
            key: "status",
            align: "center",
            priority: 2,
            render: (value) => (
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    value === 'transferd' 
                        ? 'bg-green-100 text-green-800' 
                        : value === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                    {value === 'transferd' ? 'Transferred' : 
                     value === 'pending' ? 'Pending' : value}
                </span>
            )
        },
        {
            title: "Reason",
            key: "transactionReason",
            priority: 3,
            render: (value) => (
                <span className="text-sm text-gray-600" title={value}>
                    {value || 'No reason specified'}
                </span>
            )
        },
        {
            title: "Transfer Date",
            key: "createdAt",
            priority: 2,
            render: (value) => (
                <span className="text-sm text-gray-600">
                    {formatDate(value)}
                </span>
            )
        },
        {
            title: "Product Type",
            key: "productType",
            priority: 4,
            render: (value) => (
                <span className="capitalize text-sm">
                    {value ? value.replace(/[-_]/g, ' ') : 'N/A'}
                </span>
            )
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            priority: 1,
            render: (_, row) => (
                <div className="flex justify-center">
                    {canCancelTransfers && (
                        <button
                            onClick={() => handleCancelTransfer(row._id, row.productName)}
                            disabled={isLoading}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors text-sm disabled:opacity-50"
                            title="Cancel this transfer"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            )
        }
    ];

    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        searchTransferHistory();
    };

    const content = (
        <>
            <PageHeader
                title={`Stock Transfer History${fromLocation ? ` - ${fromLocation.charAt(0).toUpperCase() + fromLocation.slice(1)}` : ''}${productType ? ` (${productType.charAt(0).toUpperCase() + productType.slice(1)})` : ''}`}
                description={`View and track${fromLocation ? ` ${fromLocation}` : ''}${productType ? ` ${productType}` : ''} stock transfer transactions`}
            />

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium">Error Loading Transfer History</h4>
                            <p className="mt-1">{error}</p>
                        </div>
                        <button
                            onClick={() => searchTransferHistory()}
                            disabled={isLoading}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {isLoading ? 'Loading...' : 'Retry'}
                        </button>
                    </div>
                </div>
            )}

            {!canReadTransfers ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading permissions...</div>
                </div>
            ) : (
                <>
                    {/* Navigation/Close Button and Bulk Operations */}
                    <div className="mb-4 flex justify-between items-center">
                        <div>
                            {isModal ? (
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                                >
                                    ✕ Close
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate("/warehouse")}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                                >
                                    ← Back to Warehouse
                                </button>
                            )}
                        </div>
                        
                        {/* Bulk Operations */}
                        {canCancelTransfers && selectedTransfers.length > 0 && (
                            <div className="flex items-center space-x-3">
                                <span className="text-sm text-gray-600">
                                    {selectedTransfers.length} transfer(s) selected
                                </span>
                                <button
                                    onClick={handleBulkCancelTransfers}
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? 'Cancelling...' : 'Cancel Selected'}
                                </button>
                            </div>
                        )}
                    </div>

                    <AppTable
                        TH={tableHeaders}
                        TD={transferHistoryData}
                        isLoading={isLoading || isSearching}
                        emptyMessage={error ? "Failed to load transfer history. Please try again." : "No stock transfers found."}
                        TableSearch={
                            <TableSearch
                                options={options}
                                onClick={handleSearch}
                                optionSelect={optionSelect}
                                setOptionSelect={setOptionSelect}
                                setValueSearched={setValueSearched}
                                valueSearched={valueSearched}
                                onClear={handleClearSearch}
                            />
                        }
                    />
                </>
            )}
        </>
    );

    if (isModal) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-5/6 overflow-hidden">
                    <div className="p-5 h-full overflow-y-auto">
                        {content}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            {content}
        </div>
    );
}

export default StockTransferHistory;